/// <amd-module name="Pipeline/Analytics/PipelineLeadTimeReport/Pipeline.LeadTimeReport" />
import { getCachedState, setCachedState } from "Analytics/Shared/Powerbi/Utils/LocalCacheManager";
import * as Powerbi from "Analytics/Shared/PowerbiClient/Contracts/Powerbi";
import { getPowerbiClient, IPowerbiRestClient } from "Analytics/Shared/PowerbiClient/RestClient/Powerbi";
import { PowerbiReport } from "Analytics/Shared/PowerbiReport/PowerbiReport";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

export interface IPipelineLeadTimeReportProps {
    definitionId: number;
}

export class PipelineLeadTimeReport extends VssComponent<IPipelineLeadTimeReportProps, {}> {
    // cache invalidate time in minutes.
    private static readonly c_cacheInvalidateTime = 180;
    private static readonly c_forwardHelpLink = "https://go.microsoft.com/fwlink/?linkid=2094586";
    private static readonly _useAutomatedWorkspaces = "Pipeline.LeadTimeMetrics.AutomatedWorkspaces";
    private static readonly c_slicerNames = ["aea8142467fbba126712","1eeac12a904e867ca792", "aeee3302313d8db6ae2b", "67b32a3d38e4a60d5490"];
    private _pageData: IPageData | undefined;
    private _hostId: string;
    private _hostName: string;
    private _projectId: string;
    private _powerBiClient: IPowerbiRestClient;

    constructor(props: IPipelineLeadTimeReportProps, context: IVssComponentContext) {
        super(props, context);
        const tfsPageData = getTFSPageData(context.pageContext);
        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        this._pageData = vssPageService.getData();
        this._hostName = this._pageData!.hostName;
        this._hostId = this._pageData!.hostId;
        this._projectId = tfsPageData!.project!.id;
        this._powerBiClient = getPowerbiClient(context.pageContext);
    }

    public shouldComponentUpdate(nextProps: IPipelineLeadTimeReportProps) {
        return this.props.definitionId !== nextProps.definitionId;
    }

    public render() {
        return (
            <PowerbiReport
                getReportDetails={(accessToken: string) => this._getReportDetails(accessToken)}
                forwardLinkOnError={PipelineLeadTimeReport.c_forwardHelpLink}
                setDefaultSlicerState={this._shouldSetDefaultSlicerState()}
            />
        );
    }

    private _shouldSetDefaultSlicerState(): boolean{
        if(this._useAutomatedWorkspaces()){
            return true;
        }

        return false;
    }

    private async _getReportDetails(token: string): Promise<Powerbi.PowerBiReportDetails> {
        const leadTimeReportPrefix = "LeadTime";
        let groupName = Utils_String.format("{0}_{1}", this._hostId, this._projectId);
        if (this._useAutomatedWorkspaces()) {
            groupName = Utils_String.format("{0}_{1}", this._hostName, this._projectId);
        }

        const reportName = Utils_String.format("{0}_{1}", leadTimeReportPrefix, this.props.definitionId);

        const cachedReport = this._getCachedReport();
        if (cachedReport) {
            return Promise.resolve(cachedReport);
        }

        const report = await this._powerBiClient.getReport(this._projectId, groupName, reportName, token);
        setCachedState(this._getEmbedUrlStorageKey(), JSON.stringify(report));
        return report;
    }

    private _getEmbedUrlStorageKey(): string {
        if (this._useAutomatedWorkspaces()) {
            Utils_String.format("{0}_{1}_{2}", this._hostName, this._projectId, this.props.definitionId);
        }

        return Utils_String.format("{0}_{1}_{2}", this._hostId, this._projectId, this.props.definitionId);
    }

    private _useAutomatedWorkspaces() {
        return isFeatureFlagEnabled(this.context.pageContext, PipelineLeadTimeReport._useAutomatedWorkspaces, false);
    }

    private _getCachedReport(): Powerbi.PowerBiReportDetails | null {
        try {
            const cachedData = getCachedState(this._getEmbedUrlStorageKey(), PipelineLeadTimeReport.c_cacheInvalidateTime);
            if (cachedData) {
                const report = JSON.parse(cachedData);
                if (report && report.embedUrl) {
                    return report;
                }
            }
            return null;
        } catch {
            return null;
        }
    }
}

Components.add("pipelineLeadTimeReport", PipelineLeadTimeReport);
